import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import AdminPanelSettings from "@mui/icons-material/AdminPanelSettings";
import { motion } from "framer-motion";
import { useLoginAdmin } from "../../hooks/api/useAdminAuth";
import { useAdminAuth } from "../../context/AdminAuthContext";
import {
  fullPageCentered,
  contentMaxWidth,
  formMaxWidth,
} from "../../styles/layoutStyles";
import { getFriendlyErrorMessage } from "../../Utils/errorMessageMap";
import { ROUTE_LOGIN } from "../../constants/routes";

const cardVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] },
  },
};

const itemVariants = {
  hidden: { opacity: 0, x: -12 },
  visible: (i: number) => ({
    opacity: 1,
    x: 0,
    transition: { delay: 0.1 + i * 0.05, duration: 0.3 },
  }),
};

interface AdminLoginPageProps {
  onSuccess: () => void;
  onAccessDenied: () => void;
}

const AdminLoginPage: React.FC<AdminLoginPageProps> = ({
  onSuccess,
  onAccessDenied,
}) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const { status } = useAdminAuth();
  const loginMutation = useLoginAdmin();
  const location = useLocation();
  const navigate = useNavigate();

  // Shared GraphQL auth redirect can append redirectPath; admin login does not use it.
  useEffect(() => {
    if (location.search.includes("redirectPath")) {
      navigate(ROUTE_LOGIN, { replace: true });
    }
  }, [location.search, navigate]);

  useEffect(() => {
    if (status === "authenticated") {
      onSuccess();
    }
  }, [status, onSuccess]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    try {
      await loginMutation.mutateAsync({ email: email.trim(), password });
      onSuccess();
    } catch (err: unknown) {
      const message =
        err && typeof err === "object" && "message" in err
          ? String((err as { message: string }).message)
          : "Login failed";
      if (message.includes("ACCESS_DENIED") || message.includes("access")) {
        onAccessDenied();
      }
    }
  };

  const isAccessDenied =
    loginMutation.isError &&
    String(loginMutation.error?.message ?? "")
      .toLowerCase()
      .includes("access");

  return (
    <Box
      sx={{
        ...fullPageCentered,
        background:
          "linear-gradient(160deg, #f5f7fa 0%, #e8eef5 50%, #dde5f0 100%)",
      }}
    >
      <motion.div
        variants={cardVariants}
        initial="hidden"
        animate="visible"
        style={{
          width: "100%",
          maxWidth: contentMaxWidth,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Card
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: 420,
            borderRadius: 3,
            boxShadow: "0 8px 32px rgba(25, 118, 210, 0.12)",
            border: "1px solid rgba(25, 118, 210, 0.08)",
          }}
        >
          <CardContent
            sx={{
              p: { xs: 3, sm: 4 },
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <motion.div
              custom={0}
              variants={itemVariants}
              initial="hidden"
              animate="visible"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 12,
                marginBottom: 24,
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  bgcolor: "primary.main",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <AdminPanelSettings fontSize="medium" />
              </Box>
              <Typography variant="h5" fontWeight={700} color="text.primary">
                Admin Portal
              </Typography>
            </motion.div>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Sign in with your super-admin account.
            </Typography>

            {isAccessDenied && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                This account does not have admin access. Use the main app for
                company accounts.
              </Alert>
            )}
            {loginMutation.isError && !isAccessDenied && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {getFriendlyErrorMessage(loginMutation.error?.message)}
              </Alert>
            )}

            <Box sx={{ width: "100%", maxWidth: formMaxWidth }}>
              <form onSubmit={handleSubmit}>
                <motion.div
                  custom={1}
                  variants={itemVariants}
                  initial="hidden"
                  animate="visible"
                >
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    sx={{ mb: 2 }}
                  />
                </motion.div>
                <motion.div
                  custom={2}
                  variants={itemVariants}
                  initial="hidden"
                  animate="visible"
                >
                  <TextField
                    fullWidth
                    label="Password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            aria-label="toggle password"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 3 }}
                  />
                </motion.div>
                <motion.div
                  custom={3}
                  variants={itemVariants}
                  initial="hidden"
                  animate="visible"
                >
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={loginMutation.isPending}
                    sx={{
                      py: 1.5,
                      fontWeight: 600,
                      textTransform: "none",
                    }}
                  >
                    {loginMutation.isPending ? "Signing in…" : "Sign in"}
                  </Button>
                </motion.div>
              </form>
            </Box>
          </CardContent>
        </Card>
      </motion.div>
    </Box>
  );
};

export default AdminLoginPage;
